/**
 * Branded identity types for the Account / Campaign / Pack container model
 * (post-2026-05-28 refactor — see `01-concepts.md`, `02-architecture.md §2.8`).
 *
 * An **Account** is the User-account scope: identity, login, billing. One
 * human User = one Account in v1. Accounts own Campaigns and Packs.
 *
 * A **Campaign** is the sovereign container: one op log, one canon, one
 * member roster, one canonical calendar, one ordered list of subscribed
 * Packs, one session cursor. Each Campaign maps to its own Durable Object.
 * Campaigns are **not** in-model entities — they are the boundary that
 * defines what the world model is.
 *
 * A **Pack** is the only shareable-content primitive. Versioned, immutable.
 *
 * We use TypeScript "brands" so the id spaces are structurally distinct.
 * Passing a `CampaignId` where an `AccountId` is expected is a compile-time
 * error. There is no runtime difference (all are `string`).
 */

import { z } from 'zod';

// `crypto.randomUUID` is available at runtime in modern browsers, Web
// Workers, Cloudflare Workers, and Node 19+ — we don't want to pull
// DOM lib types into every consumer just to type this one call. Mirrors
// the declaration in `entity.ts`.
declare const crypto: { randomUUID(): string };

declare const AccountIdBrand: unique symbol;
/**
 * Branded Account id. Format: `acct_<uuid>` (UUIDv4). The User's
 * Account is the billing + identity scope; one User maps to one
 * Account in v1.
 */
export type AccountId = string & { readonly [AccountIdBrand]: true };

export const AccountIdSchema = z
  .string()
  .min(1)
  .refine((s) => s.startsWith('acct_'), { message: 'AccountId must start with "acct_"' })
  .transform((s): AccountId => s as AccountId);

/** Mint a new AccountId. */
export function mintAccountId(): AccountId {
  return `acct_${crypto.randomUUID()}` as AccountId;
}

declare const CampaignIdBrand: unique symbol;
/**
 * Branded Campaign id. Format: `camp_<uuid>` (UUIDv4). Campaigns are
 * registered in the global D1 `campaigns` table and addressed by this
 * id when looking up a Durable Object via `idFromName`. A CampaignId
 * is **not** an in-model entity id (campaigns aren't entities) — it
 * names the sovereign container.
 */
export type CampaignId = string & { readonly [CampaignIdBrand]: true };

export const CampaignIdSchema = z
  .string()
  .min(1)
  .refine((s) => s.startsWith('camp_'), { message: 'CampaignId must start with "camp_"' })
  .transform((s): CampaignId => s as CampaignId);

/** Mint a new CampaignId. */
export function mintCampaignId(): CampaignId {
  return `camp_${crypto.randomUUID()}` as CampaignId;
}

declare const PackIdBrand: unique symbol;
/**
 * Branded Pack id. Format: namespaced strings like `core`, `dnd5e`,
 * `faerun`, `homebrew_<uuid>`. Packs are addressed by id; specific
 * snapshots are addressed by (id, version) — see `PackVersion`.
 */
export type PackId = string & { readonly [PackIdBrand]: true };

export const PackIdSchema = z
  .string()
  .min(1)
  .regex(/^[a-z0-9_][a-z0-9_-]*$/, { message: 'PackId must be lowercase kebab/snake with optional digits' })
  .transform((s): PackId => s as PackId);

/** Mint a new homebrew PackId (`homebrew_<uuid>`). */
export function mintHomebrewPackId(): PackId {
  return `homebrew_${crypto.randomUUID()}` as PackId;
}
